/**
 * How many tabs of this browser have a round open — a diagnostic for the
 * message-processing stall report (SP-2823: the incident was ~40 tabs of one
 * round; the report should say so without asking the user).
 *
 * A tab counts while its round client is LIVE — wired to the round page and
 * not torn down. The round page's ClientWrapper holds a presence when it
 * wires the client (first mount and each in-place reload) and releases it
 * when it tears the client down: on unmount (navigating to a non-round
 * route), on a terminal disconnect (stale build, schema skew — the client
 * will never apply another confirmation), and before re-holding for a
 * replacement client (round switch, impersonation switch, profile refetch).
 * A tab that closes or navigates away has its lock released by the browser.
 *
 * Deliberately NOT the socket's own connect/disconnect events: a tab whose
 * socket is mid-reconnect is still live and applies its whole catch-up
 * burst the moment the socket returns — it is a contender, and dropping it
 * would make the count flap in exactly the reconnect storms where stalls
 * happen. The count is "tabs that will apply confirmations", not "tabs with
 * an open socket this instant".
 *
 * The presence is a Web Lock named for the round and client id, in shared
 * mode with a unique name, so holding it can neither block nor be blocked;
 * `navigator.locks.query()` is then an exact live count with no heartbeats
 * and no expiry to tune. The held request's promise settles only through the
 * returned release and is never awaited — awaiting it from any pipeline
 * would hang it. A page shows one round at a time, so the module also keeps
 * at most one presence per page (taking a new one releases the previous),
 * and names are counted distinct so a release still in flight cannot
 * double-count the same tab. Where the API is missing (older WebKit, node,
 * tests) the count is simply unknown.
 */
const LOCK_PREFIX = "sp-round-tab:";

// Settles the callback promise of the presence this page currently holds,
// which releases its lock.
let releaseCurrent: (() => void) | null = null;

function lockManager(): LockManager | null {
  const locks = (globalThis as { navigator?: { locks?: LockManager } })
    .navigator?.locks;
  return locks == null ? null : locks;
}

/**
 * Marks this client as an open tab of `aggregateId`. Returns the release;
 * releasing a presence that a later hold already superseded is a no-op.
 */
export function holdRoundTabPresence(
  aggregateId: string,
  clientId: string,
): () => void {
  const locks = lockManager();
  if (locks == null) return () => {};
  releaseCurrent?.();
  let release: () => void = () => {};
  const held = new Promise<void>((resolve) => {
    release = resolve;
  });
  releaseCurrent = release;
  void locks
    .request(
      `${LOCK_PREFIX}${aggregateId}:${clientId}`,
      { mode: "shared" },
      () => held,
    )
    .catch(() => {
      // Denied (e.g. Lockdown Mode) — the count is just unknown for this tab.
    });
  return release;
}

/**
 * Tabs of this browser currently holding `aggregateId` open, this one
 * included. Returns null synchronously when the API is unavailable, so a
 * caller that must report without deferring can tell the two apart.
 */
export function countRoundTabs(
  aggregateId: string,
): Promise<number | null> | null {
  const locks = lockManager();
  if (locks == null) return null;
  const prefix = `${LOCK_PREFIX}${aggregateId}:`;
  return locks
    .query()
    .then(
      ({ held }) =>
        new Set(
          (held ?? [])
            .map((lock) => lock.name)
            .filter(
              (name): name is string => name?.startsWith(prefix) === true,
            ),
        ).size,
    )
    .catch(() => null);
}
